<template>
  <div class="fund">

    <!-- ─── Hero ─────────────────────────────────────────────────────────── -->
    <header class="fund__hero">
      <div class="fund__hero-bg"></div>
      <div class="fund__hero-glow"></div>
      <div class="fund__hero-inner">
        <div class="fund__hero-badge">
          <span class="fund__hero-badge-dot"><span class="fund__hero-badge-ping"></span></span>
          SHC Capital · {{ $t('fund.badge') }}
        </div>
        <h1 class="fund__hero-title">{{ $t('fund.heroTitle') }}</h1>
        <p class="fund__hero-desc">{{ $t('fund.heroDesc') }}</p>
        <div class="fund__hero-actions">
          <button class="fund__btn fund__btn--outline" @click="goToContact">
            {{ $t('fund.contactBtn') }}
          </button>
        </div>
      </div>
      <div class="fund__hero-wave">
        <svg viewBox="0 0 1440 80" fill="none" preserveAspectRatio="none">
          <path
              d="M0 80L60 65C120 50 240 20 360 10C480 0 600 0 720 7.5C840 15 960 30 1080 37.5C1200 45 1320 45 1380 45L1440 45V80H0Z"
              fill="#f2f0ea"/>
        </svg>
      </div>
    </header>

    <!-- ─── Stats bar ─────────────────────────────────────────────────────── -->
    <section class="fund__stats-bar">
      <div class="fund__shell">
        <div class="fund__stats-grid">
          <div v-for="(stat, i) in stats" :key="i" class="fund__stat">
            <span class="fund__stat-value">{{ stat.value }}</span>
            <span class="fund__stat-label">{{ $t(stat.labelKey) }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- ─── Overview ─────────────────────────────────────────────────────── -->
    <section class="fund__section fund__section--light">
      <div class="fund__shell">
        <div class="fund__section-header">
          <h2 class="fund__section-title">{{ $t('fund.overviewTitle') }}</h2>
          <div class="fund__divider"></div>
        </div>
        <div class="fund__overview-grid">
          <div v-for="(item, i) in overviewItems" :key="i" class="fund__overview-card">
            <div class="fund__overview-icon">
              <i :class="item.icon"></i>
            </div>
            <h3 class="fund__overview-card-title">{{ $t(item.titleKey) }}</h3>
            <p class="fund__overview-card-desc">{{ $t(item.descKey) }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ─── How it works ─────────────────────────────────────────────────── -->
    <section class="fund__section fund__section--white">
      <div class="fund__shell">
        <div class="fund__section-header">
          <h2 class="fund__section-title">{{ $t('fund.howTitle') }}</h2>
          <p class="fund__section-sub">{{ $t('fund.howSub') }}</p>
          <div class="fund__divider"></div>
        </div>
        <div class="fund__steps">
          <div v-for="(step, i) in steps" :key="i" class="fund__step">
            <div class="fund__step-num">{{ String(i + 1).padStart(2, '0') }}</div>
            <div class="fund__step-body">
              <h3 class="fund__step-title">{{ $t(step.titleKey) }}</h3>
              <p class="fund__step-desc">{{ $t(step.descKey) }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ─── Risk & Return ─────────────────────────────────────────────────── -->
    <section class="fund__section fund__section--light">
      <div class="fund__shell">
        <div class="fund__section-header">
          <h2 class="fund__section-title">{{ $t('fund.riskTitle') }}</h2>
          <div class="fund__divider"></div>
        </div>
        <div class="fund__risk-grid">
          <div v-for="(row, i) in riskRows" :key="i" class="fund__risk-row"
               :class="{ 'fund__risk-row--featured': row.featured }">
            <div class="fund__risk-left">
              <i :class="row.icon" class="fund__risk-icon"></i>
              <strong>{{ $t(row.labelKey) }}</strong>
            </div>
            <div class="fund__risk-right">
              <em>{{ row.value }}</em>
              <small>{{ $t(row.noteKey) }}</small>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ─── PDF Download CTA ──────────────────────────────────────────────── -->
    <section class="fund__pdf-cta">
      <div class="fund__shell">
        <div class="fund__pdf-box">
          <div class="fund__pdf-icon">
            <i class="fas fa-file-pdf"></i>
          </div>
          <div class="fund__pdf-text">
            <h3>{{ $t('fund.pdfTitle') }}</h3>
            <p>{{ $t('fund.pdfDesc') }}</p>
          </div>
          <a
              :href="
                $i18n.locale === 'vi'
                  ? 'https://songhancapital.com/assets/pdf/SHC_EA_PRO_INVESTMENT_FUND.pdf'
                  : 'https://songhancapital.com/assets/pdf/SHC_EA_PRO_INVESTMENT_FUND_en.pdf'
        "
              target="_blank"
              rel="noopener noreferrer"
              class="fund__btn fund__btn--primary fund__btn--lg"
          >
            <i class="fas fa-external-link-alt"></i>
            {{ $t('fund.downloadBtn') }}
          </a>
        </div>
      </div>
    </section>

    <!-- ─── FAQ ───────────────────────────────────────────────────────────── -->
    <section class="fund__section fund__section--white">
      <div class="fund__shell fund__shell--narrow">
        <div class="fund__section-header">
          <h2 class="fund__section-title">{{ $t('fund.faqTitle') }}</h2>
          <div class="fund__divider"></div>
        </div>
        <div class="fund__faq">
          <div
              v-for="(faq, i) in faqs"
              :key="i"
              class="fund__faq-item"
              :class="{ 'is-open': activeFaq === i }"
              @click="toggleFaq(i)"
          >
            <div class="fund__faq-question">
              <span>{{ $t(faq.qKey) }}</span>
              <em class="fund__faq-icon" :class="{ open: activeFaq === i }">+</em>
            </div>
            <div v-if="activeFaq === i" class="fund__faq-answer">{{ $t(faq.aKey) }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- ─── CTA ───────────────────────────────────────────────────────────── -->
    <section class="fund__cta">
      <div class="fund__shell">
        <h2 class="fund__cta-title">{{ $t('fund.ctaTitle') }}</h2>
        <p class="fund__cta-desc">{{ $t('fund.ctaDesc') }}</p>
        <div class="fund__cta-actions">
          <button class="fund__btn fund__btn--primary" @click="goToContact">
            {{ $t('fund.ctaPrimary') }}
          </button>
        </div>
      </div>
    </section>

  </div>
</template>

<script src="./fund.js"></script>
<style src="./fund.scss" lang="scss"></style>
